import type { Locale } from "./homepage-seo.ts";

const en = {
  parserChoiceTitle: "Choose how to read your rules",
  parserChoiceBody: "The rule parser is always available. AI parsing requires sign-in and an available AI credit.",
  ruleParser: "Rule parser",
  ruleParserHint: "Deterministic parsing without an AI credit.",
  aiParserReady: "AI parser",
  aiParserHint: "Uses one available AI credit.",
  builderApplyFirst: "Review and apply the visible rules before running this backtest.",
  invalidDateOrder: "The start date must be on or before the end date.",
  useAllowedDates: "Use allowed dates",
  dateAdjusted: "The start date was adjusted from {previous} to {current} for the current timeframe and plan.",
};

type FlowRecoveryKey = keyof typeof en;
type FlowRecoveryCopy = Record<FlowRecoveryKey, string>;

const translations: Record<Locale, FlowRecoveryCopy> = {
  en,
  vi: {
    parserChoiceTitle: "Chọn cách đọc bộ quy tắc",
    parserChoiceBody: "Bộ đọc quy tắc luôn dùng được. Phân tích bằng AI cần đăng nhập và còn lượt AI.",
    ruleParser: "Bộ đọc quy tắc",
    ruleParserHint: "Đọc cố định, không dùng lượt AI.",
    aiParserReady: "Bộ đọc AI",
    aiParserHint: "Dùng một lượt AI còn khả dụng.",
    builderApplyFirst: "Hãy xem và xác nhận bộ quy tắc đang hiển thị trước khi chạy backtest.",
    invalidDateOrder: "Ngày bắt đầu phải trước hoặc trùng ngày kết thúc.",
    useAllowedDates: "Dùng khoảng ngày cho phép",
    dateAdjusted: "Ngày bắt đầu đã được đổi từ {previous} thành {current} theo timeframe và gói hiện tại.",
  },
  hi: {
    parserChoiceTitle: "नियम पढ़ने का तरीका चुनें",
    parserChoiceBody: "Rule parser हमेशा उपलब्ध है। AI parsing के लिए sign-in और AI credit चाहिए।",
    ruleParser: "Rule parser",
    ruleParserHint: "AI credit के बिना निश्चित parsing।",
    aiParserReady: "AI parser",
    aiParserHint: "एक उपलब्ध AI credit उपयोग करता है।",
    builderApplyFirst: "Backtest चलाने से पहले दिख रहे नियमों की समीक्षा करके लागू करें।",
    invalidDateOrder: "आरंभ तिथि, समाप्ति तिथि से पहले या उसी दिन होनी चाहिए।",
    useAllowedDates: "मान्य तारीखें उपयोग करें",
    dateAdjusted: "वर्तमान timeframe और plan के लिए आरंभ तिथि {previous} से {current} की गई।",
  },
  ur: {
    parserChoiceTitle: "قواعد پڑھنے کا طریقہ چنیں",
    parserChoiceBody: "Rule parser ہمیشہ دستیاب ہے۔ AI parsing کے لیے sign-in اور AI credit درکار ہے۔",
    ruleParser: "Rule parser",
    ruleParserHint: "AI credit کے بغیر مقررہ parsing۔",
    aiParserReady: "AI parser",
    aiParserHint: "ایک دستیاب AI credit استعمال کرتا ہے۔",
    builderApplyFirst: "Backtest چلانے سے پہلے دکھائی گئی قواعد کا جائزہ لے کر لاگو کریں۔",
    invalidDateOrder: "شروع کی تاریخ اختتامی تاریخ سے پہلے یا اسی دن ہونی چاہیے۔",
    useAllowedDates: "اجازت یافتہ تاریخیں استعمال کریں",
    dateAdjusted: "موجودہ timeframe اور plan کے مطابق تاریخ {previous} سے {current} کی گئی۔",
  },
  th: {
    parserChoiceTitle: "เลือกวิธีอ่านกฎ",
    parserChoiceBody: "ตัวอ่านกฎใช้ได้เสมอ ส่วน AI ต้องเข้าสู่ระบบและมีเครดิตเหลือ",
    ruleParser: "ตัวอ่านกฎ",
    ruleParserHint: "อ่านแบบกำหนดตายตัวโดยไม่ใช้เครดิต AI",
    aiParserReady: "ตัวอ่าน AI",
    aiParserHint: "ใช้เครดิต AI ที่มีหนึ่งครั้ง",
    builderApplyFirst: "ตรวจสอบและยืนยันกฎที่แสดงก่อนรันแบ็กเทสต์",
    invalidDateOrder: "วันที่เริ่มต้องไม่อยู่หลังวันที่สิ้นสุด",
    useAllowedDates: "ใช้ช่วงวันที่ที่อนุญาต",
    dateAdjusted: "ปรับวันที่เริ่มจาก {previous} เป็น {current} ตาม timeframe และแพ็กเกจปัจจุบัน",
  },
  bn: {
    parserChoiceTitle: "নিয়ম পড়ার পদ্ধতি বেছে নিন",
    parserChoiceBody: "Rule parser সবসময় পাওয়া যায়। AI parsing-এর জন্য sign-in ও AI credit দরকার।",
    ruleParser: "Rule parser",
    ruleParserHint: "AI credit ছাড়া নির্ধারিত parsing।",
    aiParserReady: "AI parser",
    aiParserHint: "একটি উপলভ্য AI credit ব্যবহার করে।",
    builderApplyFirst: "Backtest চালানোর আগে দৃশ্যমান নিয়ম পর্যালোচনা করে প্রয়োগ করুন।",
    invalidDateOrder: "শুরুর তারিখ শেষের তারিখের আগে বা একই দিনে হতে হবে।",
    useAllowedDates: "অনুমোদিত তারিখ ব্যবহার করুন",
    dateAdjusted: "বর্তমান timeframe ও plan অনুযায়ী শুরুর তারিখ {previous} থেকে {current} করা হয়েছে।",
  },
  id: {
    parserChoiceTitle: "Pilih cara membaca aturan",
    parserChoiceBody: "Parser aturan selalu tersedia. Parser AI memerlukan login dan kredit AI.",
    ruleParser: "Parser aturan",
    ruleParserHint: "Parsing deterministik tanpa kredit AI.",
    aiParserReady: "Parser AI",
    aiParserHint: "Memakai satu kredit AI yang tersedia.",
    builderApplyFirst: "Tinjau dan terapkan aturan yang terlihat sebelum menjalankan backtest.",
    invalidDateOrder: "Tanggal mulai harus sebelum atau sama dengan tanggal selesai.",
    useAllowedDates: "Gunakan tanggal yang diizinkan",
    dateAdjusted: "Tanggal mulai diubah dari {previous} menjadi {current} sesuai timeframe dan paket.",
  },
  fil: {
    parserChoiceTitle: "Piliin kung paano babasahin ang rules",
    parserChoiceBody: "Laging available ang rule parser. Kailangan ng sign-in at AI credit para sa AI parser.",
    ruleParser: "Rule parser",
    ruleParserHint: "Deterministic parsing na walang AI credit.",
    aiParserReady: "AI parser",
    aiParserHint: "Gumagamit ng isang available na AI credit.",
    builderApplyFirst: "I-review at i-apply ang nakikitang rules bago patakbuhin ang backtest.",
    invalidDateOrder: "Dapat mas maaga o kapareho ng end date ang start date.",
    useAllowedDates: "Gamitin ang pinapayagang petsa",
    dateAdjusted: "Inayos ang start date mula {previous} papuntang {current} para sa timeframe at plan.",
  },
  "es-419": {
    parserChoiceTitle: "Elige cómo interpretar tus reglas",
    parserChoiceBody: "El parser de reglas siempre está disponible. El parser con IA requiere sesión y crédito.",
    ruleParser: "Parser de reglas",
    ruleParserHint: "Interpretación determinista sin crédito de IA.",
    aiParserReady: "Parser con IA",
    aiParserHint: "Usa un crédito de IA disponible.",
    builderApplyFirst: "Revisa y aplica las reglas visibles antes de ejecutar el backtest.",
    invalidDateOrder: "La fecha inicial debe ser anterior o igual a la fecha final.",
    useAllowedDates: "Usar fechas permitidas",
    dateAdjusted: "La fecha inicial cambió de {previous} a {current} según el timeframe y el plan.",
  },
  "pt-BR": {
    parserChoiceTitle: "Escolha como interpretar as regras",
    parserChoiceBody: "O parser de regras está sempre disponível. O parser com IA exige login e crédito.",
    ruleParser: "Parser de regras",
    ruleParserHint: "Interpretação determinística sem crédito de IA.",
    aiParserReady: "Parser com IA",
    aiParserHint: "Usa um crédito de IA disponível.",
    builderApplyFirst: "Revise e aplique as regras visíveis antes de executar o backtest.",
    invalidDateOrder: "A data inicial deve ser anterior ou igual à data final.",
    useAllowedDates: "Usar datas permitidas",
    dateAdjusted: "A data inicial mudou de {previous} para {current} conforme o timeframe e o plano.",
  },
  ja: {
    parserChoiceTitle: "ルールの読み取り方法を選択",
    parserChoiceBody: "ルールパーサーは常に利用できます。AIにはサインインとAIクレジットが必要です。",
    ruleParser: "ルールパーサー",
    ruleParserHint: "AIクレジットを使わない決定的な解析です。",
    aiParserReady: "AIパーサー",
    aiParserHint: "利用可能なAIクレジットを1回使います。",
    builderApplyFirst: "バックテスト前に表示中のルールを確認して適用してください。",
    invalidDateOrder: "開始日は終了日以前にしてください。",
    useAllowedDates: "許可された日付を使う",
    dateAdjusted: "現在の時間足とプランに合わせ、開始日を{previous}から{current}へ変更しました。",
  },
  ko: {
    parserChoiceTitle: "규칙 해석 방법 선택",
    parserChoiceBody: "규칙 파서는 항상 사용할 수 있습니다. AI 파서는 로그인과 AI 크레딧이 필요합니다.",
    ruleParser: "규칙 파서",
    ruleParserHint: "AI 크레딧을 쓰지 않는 결정적 해석입니다.",
    aiParserReady: "AI 파서",
    aiParserHint: "사용 가능한 AI 크레딧 1개를 사용합니다.",
    builderApplyFirst: "백테스트 전에 표시된 규칙을 검토하고 적용하세요.",
    invalidDateOrder: "시작일은 종료일과 같거나 이전이어야 합니다.",
    useAllowedDates: "허용된 날짜 사용",
    dateAdjusted: "현재 시간 프레임과 요금제에 맞춰 시작일을 {previous}에서 {current}로 조정했습니다.",
  },
  ru: {
    parserChoiceTitle: "Выберите способ чтения правил",
    parserChoiceBody: "Парсер правил доступен всегда. Для AI нужны вход и доступный кредит.",
    ruleParser: "Парсер правил",
    ruleParserHint: "Детерминированный разбор без AI-кредита.",
    aiParserReady: "AI-парсер",
    aiParserHint: "Использует один доступный AI-кредит.",
    builderApplyFirst: "Проверьте и примените видимые правила перед запуском бэктеста.",
    invalidDateOrder: "Дата начала должна быть не позже даты окончания.",
    useAllowedDates: "Использовать допустимые даты",
    dateAdjusted: "Дата начала изменена с {previous} на {current} для текущего таймфрейма и плана.",
  },
  tr: {
    parserChoiceTitle: "Kuralların nasıl okunacağını seçin",
    parserChoiceBody: "Kural ayrıştırıcı her zaman kullanılabilir. AI için giriş ve kredi gerekir.",
    ruleParser: "Kural ayrıştırıcı",
    ruleParserHint: "AI kredisi kullanmayan deterministik ayrıştırma.",
    aiParserReady: "AI ayrıştırıcı",
    aiParserHint: "Bir kullanılabilir AI kredisi kullanır.",
    builderApplyFirst: "Backtest çalıştırmadan önce görünen kuralları inceleyip uygulayın.",
    invalidDateOrder: "Başlangıç tarihi bitiş tarihinden sonra olamaz.",
    useAllowedDates: "İzin verilen tarihleri kullan",
    dateAdjusted: "Başlangıç tarihi mevcut zaman dilimi ve plana göre {previous} yerine {current} yapıldı.",
  },
  uk: {
    parserChoiceTitle: "Виберіть спосіб читання правил",
    parserChoiceBody: "Парсер правил доступний завжди. Для AI потрібні вхід і доступний кредит.",
    ruleParser: "Парсер правил",
    ruleParserHint: "Детермінований розбір без AI-кредиту.",
    aiParserReady: "AI-парсер",
    aiParserHint: "Використовує один доступний AI-кредит.",
    builderApplyFirst: "Перевірте й застосуйте видимі правила перед запуском бектесту.",
    invalidDateOrder: "Дата початку має бути не пізніше дати завершення.",
    useAllowedDates: "Використати дозволені дати",
    dateAdjusted: "Дату початку змінено з {previous} на {current} відповідно до таймфрейму й плану.",
  },
};

export function flowRecoveryT(
  locale: Locale,
  key: FlowRecoveryKey,
  parameters: Record<string, string | number> = {},
): string {
  return Object.entries(parameters).reduce(
    (value, [parameter, replacement]) =>
      value.split(`{${parameter}}`).join(String(replacement)),
    translations[locale][key],
  );
}

export function flowRecoveryLocalesAreComplete(locales: readonly Locale[]): boolean {
  const keys = Object.keys(en).sort().join("|");
  return locales.every((locale) => Object.keys(translations[locale]).sort().join("|") === keys);
}
